import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAccountDeletion } from '../../hooks/useAccountDeletion';
import { useAuth } from '../../hooks/useAuth';
import { DeleteAccountDialog } from './DeleteAccountDialog';
import { extractAxiosErrorMessage } from '../../utils/errorUtils';

export const DeleteAccountSection: React.FC = () => {
  const { signOut } = useAuth();
  const {
    isDeletionPending,
    deletionScheduledAt,
    loading,
    error: fetchError,
    requestDeletion,
    cancelDeletion,
  } = useAccountDeletion();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleConfirm = async (password: string) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await requestDeletion(password);
      setDialogOpen(false);
      signOut();
    } catch (err: unknown) {
      setSubmitError(extractAxiosErrorMessage(err, 'Failed to delete account'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelDeletion = async () => {
    setIsCancelling(true);
    setCancelError(null);
    try {
      await cancelDeletion();
    } catch (err: unknown) {
      setCancelError(extractAxiosErrorMessage(err, 'Failed to cancel deletion. Please try again.'));
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const formattedDate = deletionScheduledAt
    ? new Date(deletionScheduledAt).toLocaleDateString()
    : null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'error.main',
        borderRadius: 1,
        p: 3,
      }}
    >
      <Typography variant="h6" color="error" gutterBottom>
        Danger Zone
      </Typography>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
      )}

      {isDeletionPending ? (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Account deletion scheduled for {formattedDate}. Your account and all data will be
            permanently deleted after this date.
          </Alert>
          {cancelError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cancelError}
            </Alert>
          )}
          <Button
            variant="outlined"
            onClick={handleCancelDeletion}
            disabled={isCancelling}
          >
            {isCancelling ? 'Cancelling...' : 'Cancel Deletion'}
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete your account and all associated data. This action initiates a 30-day
            grace period, after which all data is irreversibly erased.
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDialogOpen(true)}
          >
            Delete My Account
          </Button>
        </Box>
      )}

      <DeleteAccountDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        isSubmitting={isSubmitting}
        error={submitError}
      />
    </Box>
  );
};
